import { FormikContextType, useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AppState } from 'models/app-store';
import { IWhoAmIResponse } from 'models/http/auth';
import { IUpdateUserRequest } from 'models/http/user';
import { ISession } from 'models/session';
import { SettingsCategories } from 'models/settings';
import { ICompanyOptions } from 'pages/Layout/Header/ChangeViewDropdown/ChangeViewDropdown.model';
import * as authActions from 'store/actions/auth';
import * as uiActions from 'store/actions/ui';
import useFlaggedCallback from 'utils/hooks/use-flagged-callback';
import useHttp from 'utils/hooks/use-http';
import useUnmoutedFlag from 'utils/hooks/use-unmounted-flag';
import { MFormPageProps } from 'views/MForm/MForm.model';

import { IUserProfileSecurityFormFields, userProfileSecurityInitialValues, userProfileSecurityValidationSchema } from './SecurityTab.model';
import SecurityTabView from './SecurityTab.view';

type Props = MFormPageProps;

const SecurityTab: React.FC<Props> = () => {
  const { t } = useTranslation();
  const http = useHttp();
  const dispatch = useDispatch();
  const unmountedFlagRef = useUnmoutedFlag();
  const sessionData: ISession = useSelector((state: AppState) => state.auth.session!);
  const [userIdState, setUserIdState] = useState<string>('');
  const [isLoadingState, setIsLoadingState] = useState<boolean>(false);
  const [is2FAEnforceOriginalState, setIs2FAEnforceOriginalState] = useState<boolean>(false);
  const isSite2FAEnforced = useSelector((state: AppState) => !!state.settings.settings?.[SettingsCategories.Auth].is2FAEnforced);

  const sitesCount = useMemo(() => {
    const company = sessionData.companies.find((company: ICompanyOptions) => sessionData.view.company === company.id);
    const currentCompanySites = company?.sites || [];

    return currentCompanySites.length;
  }, [sessionData]);

  const form: FormikContextType<IUserProfileSecurityFormFields> = useFormik<IUserProfileSecurityFormFields>({
    initialValues: userProfileSecurityInitialValues,
    validationSchema: userProfileSecurityValidationSchema,
    onSubmit(values: IUserProfileSecurityFormFields) {
      if (!userIdState) {
        return;
      }

      const currentUser: IUpdateUserRequest = {};

      if (!isSite2FAEnforced) {
        /** Updating is2FAEnforced only when site is not enforcing it */
        currentUser.is2FAEnforced = values.is2FAEnforced;
      }

      setIsLoadingState(() => true);

      http.user
        .update(Number(userIdState), currentUser)
        .then(() => {
          if (unmountedFlagRef.current) {
            return;
          }

          dispatch(uiActions.showSuccessToast({ message: t('userProfile.toast.success') }));

          if (values.is2FAEnforced !== is2FAEnforceOriginalState) {
            setIs2FAEnforceOriginalState(() => values.is2FAEnforced);
          }

          /** Renew session to reload user info + react to any 2FA changes */
          dispatch(authActions.autoLogin());
        })
        .catch((error) => {
          console.error(error);

          if (!unmountedFlagRef.current) {
            dispatch(uiActions.showErrorToast({ message: t('userProfile.toast.error') }));
          }
        })
        .finally(() => {
          if (!unmountedFlagRef.current) {
            setIsLoadingState(() => false);
          }
        });
    },
  });

  useEffect(() => {
    if (!sessionData?.accessToken) {
      return;
    }

    let abortController: AbortController | null = new AbortController();

    setIsLoadingState(() => true);

    http.auth
      .whoAmI(abortController, sessionData.accessToken)
      .then((me: IWhoAmIResponse) => {
        form.setValues(() => ({ is2FAEnforced: me.is2FAEnforced }));
        setUserIdState(() => me.id);
        setIs2FAEnforceOriginalState(() => me.is2FAEnforced);
      })
      .catch((error: string) => {
        console.error(error);
        dispatch(uiActions.showErrorToast({ message: t('userProfile.toast.error') }));
      })
      .finally(() => {
        abortController = null;
        setIsLoadingState(() => false);
      });

    return () => abortController?.abort();
  }, []);

  const handleIs2FAEnforcedToggle = useFlaggedCallback(
    (is2FAEnforced: boolean) => form.setFieldValue('is2FAEnforced', is2FAEnforced),
    [form.setFieldValue],
  );

  return (
    <SecurityTabView
      formInstance={form}
      isLoading={isLoadingState}
      isSite2FAEnforced={isSite2FAEnforced}
      onIs2FAEnforcedToggle={handleIs2FAEnforcedToggle}
      display2FAWarning={form.values.is2FAEnforced && !is2FAEnforceOriginalState}
      multipleSitesInfo={sitesCount > 1}
    ></SecurityTabView>
  );
};

SecurityTab.displayName = 'SecurityTab';
SecurityTab.defaultProps = {};

export default SecurityTab;
